import { useInView } from 'react-intersection-observer';

import { isZigzag } from '@common/device-manager';
import { QrLandingPopup, usePopupDialog } from '@common/overlay-manager';
import { withImageOptimizationQuery } from '@common/utils';
import { useCatalogProductIdParams, usePdpBaseInfo } from '@domains/pdp-common';

import { useExclusiveBannerUBL } from '../log/useExclusiveBannerUBL';

interface VerticalPageBannerData {
  title?: { text: string } | null;
  content?: { text: string } | null;
  image_url: string | null;
  landing_url: string | null;
}

export const useExclusiveBannerLogic = (vertical_page_banner: VerticalPageBannerData | null | undefined) => {
  const title = vertical_page_banner?.title?.text ?? '';
  const { bannerImpressionUBL, bannerClickUBL } = useExclusiveBannerUBL();
  const { open } = usePopupDialog();

  const { ref } = useInView({
    onChange: (inView) => {
      if (inView) {
        bannerImpressionUBL(convertToTitle(title));
      }
    },
    triggerOnce: true,
  });

  const handleClick = () => {
    if (vertical_page_banner?.landing_url) {
      bannerClickUBL(convertToTitle(title));

      if (vertical_page_banner.landing_url.startsWith('zigzag://')) {
        if (!isZigzag()) {
          open(QrLandingPopup());
          return;
        }
      }
      // 앱 환경이거나 일반 웹 링크인 경우
      window.location.assign(vertical_page_banner.landing_url);
    }
  };

  const convertToTitle = (title: string) => {
    if (title.includes('셀렉티드')) {
      return 'selected_home';
    }
    if (title.includes('자체제작')) {
      return 'self_production';
    }
    if (title.includes('해외브랜드')) {
      return 'global';
    }
    return '';
  };

  const optimized_vertical_page_banner = vertical_page_banner
    ? {
        ...vertical_page_banner,
        image_url:
          withImageOptimizationQuery(vertical_page_banner.image_url, { format: 'webp', quality: 80 }) ??
          vertical_page_banner.image_url,
      }
    : vertical_page_banner;

  return {
    ref,
    vertical_page_banner: optimized_vertical_page_banner,
    handleClick,
  };
};

export const useExclusiveBannerService = () => {
  const {
    product: { vertical_page_banner },
  } = usePdpBaseInfo(useCatalogProductIdParams());

  return useExclusiveBannerLogic(vertical_page_banner);
};
